import { isAbortError } from "./types";

export type QueueRequest = (fn: () => Promise<unknown>) => void;

const getAbortSignalFromArgs = (args: unknown[]): AbortSignal | undefined => {
	const lastArg = args[args.length - 1];
	return (lastArg as { signal?: AbortSignal } | undefined)?.signal;
};

// A request that keeps failing once connectivity returns is re-queued by the same branch
// that queued it, so without a bound it loops for as long as the page lives.
export const MAX_NETWORK_RETRY_ATTEMPTS = 3;

/**
 * Wraps a service with automatic retry-on-network-failure behaviour.
 * Requires a `queueRequest` callback — typically `networkManager.queueRequest` from the app —
 * which defers the retry until connectivity is restored.
 * If `queueRequest` is omitted, the service is returned unwrapped.
 * Retries are capped at `maxAttempts`; the last network error is thrown once it is reached.
 */
export const createNetworkAwareService = <T extends object>(
	service: T,
	queueRequest?: QueueRequest,
	maxAttempts: number = MAX_NETWORK_RETRY_ATTEMPTS,
): T => {
	if (!queueRequest) return service;

	return new Proxy(service, {
		get(target: T, prop: string | symbol, receiver: unknown) {
			const originalMethod = Reflect.get(target, prop, receiver);

			if (typeof originalMethod === "function" && ["get", "post", "put", "delete"].includes(prop as string)) {
				return async function (...args: unknown[]) {
					const retry = async (t: T, a: unknown[], attemptsLeft: number): Promise<unknown> => {
						try {
							return await (originalMethod as (...a: unknown[]) => Promise<unknown>).apply(t, a);
						} catch (err) {
							const abortSignal = getAbortSignalFromArgs(a);
							if (isAbortError(err) || abortSignal?.aborted) throw err;
							if (err instanceof Error && (/Failed to fetch/.test(err.message) || err.message === "Load failed")) {
								if (attemptsLeft <= 0) throw err;
								return new Promise((resolve, reject) => {
									queueRequest!(() =>
										retry(t, a, attemptsLeft - 1)
											.then(resolve)
											.catch(reject),
									);
								});
							}
							throw err;
						}
					};
					return retry(target, args, Math.max(0, maxAttempts - 1));
				};
			}
			return originalMethod;
		},
	});
};
